import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';

/**
 * WHICH signed-in identity this session acts as.
 *
 * THE BUG THIS EXISTS FOR. `setActiveAccount` was never called anywhere in the
 * application — not once — so `getActiveAccount()` always returned null and every
 * caller fell through to `getAllAccounts()[0]`: UserAuthService.setLoginDisplay,
 * the interceptor, ExportPrerenderService, the recovery redirect. That array is
 * ordered by MSAL's cache, not by what the user picked.
 *
 * With one cached account nobody noticed. With two, `[0]` was joel@vericure.io
 * while the user was signing in as joel@vortexcompanies.com — so the silent
 * authorize request went out anchored to the wrong identity
 * (X-AnchorMailbox Oid:33e4fa74…@c46c90fe…, sid for the vericure.io session), came
 * back 302, and the UI said "we couldn't confirm your access just now". Scope and
 * redirect_uri were correct the whole time; the ACCOUNT was wrong.
 *
 * So the choice is now recorded at sign-in and honoured everywhere. Two places
 * hold it, deliberately:
 *
 *   - MSAL's own active account, which it persists under
 *     `msal.<clientId>.active-account-filters`, so getActiveAccount() answers on
 *     later loads.
 *   - Our own plain-text home-account id, which survives MSAL cache states that
 *     the encrypted store cannot decrypt and gives us something to reconcile
 *     against when MSAL comes back with an account list but no active account.
 *
 * AND, when neither can say, this REFUSES TO GUESS. Picking [0] is exactly the
 * defect above; acting as the wrong user is worse than asking again.
 */
export const ACTIVE_HOME_ACCOUNT_KEY = 'vericureActiveHomeAccountId';

/** Record the identity the user actually signed in as. */
export function rememberActiveAccount(
  instance: IPublicClientApplication | null | undefined,
  account: AccountInfo | null | undefined
): void {
  if (!account?.homeAccountId) {
    return;
  }
  try {
    instance?.setActiveAccount(account);
  } catch {
    // An uninitialised instance must not break a login callback.
  }
  try {
    localStorage.setItem(ACTIVE_HOME_ACCOUNT_KEY, account.homeAccountId);
  } catch {
    // Storage blocked; MSAL's own active account still carries the choice.
  }
}

/**
 * Forget the choice — on sign-out, and before a fresh `select_account` sign-in.
 *
 * Without the second case the previous identity would still be remembered while
 * the user is in the middle of picking a different one.
 */
export function forgetActiveAccount(): void {
  try {
    localStorage.removeItem(ACTIVE_HOME_ACCOUNT_KEY);
  } catch {
    // best effort
  }
}

function remembered(): string | null {
  try {
    return localStorage.getItem(ACTIVE_HOME_ACCOUNT_KEY);
  } catch {
    return null;
  }
}

/**
 * Why resolution came out the way it did.
 *
 * The UI needs the REASON, not just the answer. "Signed out" and "signed in as
 * one of two and we cannot tell which" both yield a null account, but they are
 * different situations and a user shown the wrong one is left guessing — which is
 * exactly the mystery bounce this whole area keeps producing.
 */
export type AccountResolution =
  | { kind: 'resolved'; account: AccountInfo }
  | { kind: 'none'; account: null }
  /** MSAL cannot answer yet (not initialised). Says nothing about the user. */
  | { kind: 'unavailable'; account: null }
  /** More than one signed-in account and no record of which was chosen. */
  | { kind: 'ambiguous'; account: null; candidates: AccountInfo[] };

/** Resolution WITH its reason. `resolveActiveAccount` is the account-only view. */
export function describeActiveAccount(
  instance: IPublicClientApplication | null | undefined
): AccountResolution {
  if (!instance) {
    return { kind: 'unavailable', account: null };
  }

  let accounts: AccountInfo[] = [];
  try {
    const active = instance.getActiveAccount();
    if (active) {
      return { kind: 'resolved', account: active };
    }
    accounts = instance.getAllAccounts() ?? [];
  } catch {
    // MSAL not initialised yet. The interceptor's settled() gate is what stops
    // this being read too early; here it just means "cannot say".
    return { kind: 'unavailable', account: null };
  }

  if (accounts.length === 0) {
    return { kind: 'none', account: null };
  }

  // What the user chose, restored after a reload or a browser restart.
  const home = remembered();
  if (home) {
    const match = accounts.find((a) => a.homeAccountId === home);
    if (match) {
      rememberActiveAccount(instance, match);
      return { kind: 'resolved', account: match };
    }
  }

  // Exactly one account is not a guess — there is nothing to choose between.
  if (accounts.length === 1) {
    rememberActiveAccount(instance, accounts[0]);
    return { kind: 'resolved', account: accounts[0] };
  }

  console.warn(
    `VericureAuth: ${accounts.length} signed-in accounts and no active one ` +
      `(${accounts.map((a) => a.username).join(', ')}). NOT guessing which the ` +
      `user meant — guessing is what anchored token requests to the wrong ` +
      `identity. Sign in again to choose.`
  );
  return { kind: 'ambiguous', account: null, candidates: accounts };
}

/**
 * The account this session should act as, or null if that cannot be established.
 *
 * Null is a real answer and callers must treat it as "not signed in", NOT as a
 * cue to reach for getAllAccounts()[0] — that is the whole defect.
 */
export function resolveActiveAccount(
  instance: IPublicClientApplication | null | undefined
): AccountInfo | null {
  return describeActiveAccount(instance).account;
}

/**
 * The tenant to SHOW for an account, derived from the account itself.
 *
 * Deliberately not a GUID→name lookup table. Tenant ids are only known for the
 * tenants someone has already seen, so a table silently shows a raw GUID — or
 * nothing — for the next customer who signs in, which is the opposite of the
 * honesty this indicator is for. The username's domain is carried by every
 * account and is what the user recognises as "which company am I signed in as".
 *
 * Falls back to the tenant id, and never to an empty string: an indicator that
 * quietly shows nothing is how you end up unable to tell a wrong account from a
 * missing one.
 */
export function tenantLabelFor(account: AccountInfo | null | undefined): string {
  if (!account) {
    return '';
  }
  const domain = account.username?.split('@')[1]?.trim();
  return domain || account.tenantId || 'unknown tenant';
}
